import type { VlSupportRequest } from '../../types/ticket.types';

export type VlSupportEmailType = 'barcode_sync' | 'not_populating' | 'other' | 'need_pid';

/** People a support ticket can be tagged to and filtered by. */
export const VL_SUPPORT_OWNERS = ['BP', 'BN', 'AK', 'AV', 'JR', 'MM'] as const;

/** First letter of first and last name, e.g. Blake Nethery -> BN. Empty when a name is missing. */
export const deriveOwnerInitials = (firstName?: string | null, lastName?: string | null): string => {
  const initials = `${String(firstName ?? '').trim().charAt(0)}${String(lastName ?? '').trim().charAt(0)}`.toUpperCase();
  return initials.length === 2 ? initials : '';
};

export const VL_SUPPORT_FROM_EMAIL = 'qa@quickasyst.com';
export const VL_SUPPORT_TO_EMAIL = 'luxesupport@victorylive.com';
/** Set in non-prod .env files so the composer shows where test emails really go. */
export const VL_SUPPORT_TEST_TO_EMAIL = (process.env.REACT_APP_VL_SUPPORT_TEST_TO_EMAIL || '').trim();

export type VlSupportEventDetails = {
  eventName: string;
  /** Already formatted for display, e.g. "1/4/2027 • 7:15 PM". */
  eventDate: string;
  pid: string;
  section: string;
  row: string;
  seats: string;
};

const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const detailLines = (d: VlSupportEventDetails): string =>
  [
    `Event: ${d.eventName}`,
    `Event date: ${d.eventDate}`,
    `PID: ${d.pid}`,
    `Section: ${d.section}`,
    `Row: ${d.row}`,
    `Seats: ${d.seats}`,
  ].join('\n');

/**
 * Default email text. Edit the wording here — the composer fills it in and the
 * admin can still change it before sending.
 */
/** Account a barcode request says we tried to sync (from the team sheet). */
export type VlSupportSyncAccount = {
  method: 'season_seat_manager' | 'purchase_manager';
  team: string | null;
  platform: string | null;
};

/** Purchase Manager accounts (MLB Ballpark is the Tickets.com account). */
export const PURCHASE_MANAGER_PLATFORMS = ['Ticketmaster', 'SeatGeek', 'Paciolan', 'AXS', 'MLB Ballpark'];

export const PURCHASE_MANAGER_LABEL = 'All [PM] accounts (Ticketmaster, SeatGeek, Paciolan, AXS, MLB Ballpark)';

/** "Ticketmaster Account Manager [SSM]" / "AXS Purchase Manager" / "All Purchase Manager accounts". */
export const accountTypeLabel = (a: Pick<VlSupportSyncAccount, 'method' | 'platform'>): string => {
  if (a.method === 'season_seat_manager') return `${a.platform && a.platform !== 'Other' ? a.platform : 'Other'} [SSM]`;
  return a.platform ? `${a.platform} [PM]` : 'All [PM] accounts';
};

/** Dropdown label: "Houston Texans · Ticketmaster Account Manager [SSM]", "AXS Purchase Manager", or all accounts. */
export const syncAccountLabel = (a: VlSupportSyncAccount): string => {
  if (a.method === 'season_seat_manager' && a.team) return `${a.team} · ${accountTypeLabel(a)}`;
  return a.platform ? accountTypeLabel(a) : PURCHASE_MANAGER_LABEL;
};

/**
 * "We have attempted to sync the Houston Texans account in Season Seat Manager…",
 * "…our AXS account in Purchase Manager…", or every Purchase Manager account.
 */
export function describeSyncAttempt(accounts: VlSupportSyncAccount[] | null | undefined): string {
  const list = accounts && accounts.length ? accounts : [{ method: 'purchase_manager' as const, team: null, platform: null }];
  const teams = Array.from(
    new Set(list.filter((a) => a.method === 'season_seat_manager' && a.team).map((a) => a.team as string)),
  );
  const purchase = list.filter((a) => a.method === 'purchase_manager');
  const allPurchase = purchase.some((a) => !a.platform);
  const purchasePlatforms = Array.from(new Set(purchase.filter((a) => a.platform).map((a) => a.platform as string)));

  const parts: string[] = [];
  if (teams.length) parts.push(`the ${joinWithAnd(teams)} account${teams.length > 1 ? 's' : ''} in Season Seat Manager`);
  if (allPurchase) {
    parts.push('all of our Purchase Manager accounts (Ticketmaster, SeatGeek, Paciolan, AXS, and MLB Ballpark)');
  } else if (purchasePlatforms.length) {
    parts.push(`our ${joinWithAnd(purchasePlatforms)} account${purchasePlatforms.length > 1 ? 's' : ''} in Purchase Manager`);
  }
  return `We have attempted to sync ${parts.join(' and ')} to pull in the barcodes, and it is not working`;
}

/** Need PID: the listing is still in verification, so the PID line is left out (that is what we are asking for). */
const pidDetailLines = (d: VlSupportEventDetails): string =>
  [`Event: ${d.eventName}`, `Event date and time: ${d.eventDate}`, `Section: ${d.section}`, `Row: ${d.row}`, `Seats: ${d.seats}`].join('\n');

const PID_INTRO = 'Hi Support, we need your help mapping the correct PID for this event.';
const PID_ASK = 'Can you please provide us the correct PID so we can ingest this inventory correctly?';

const BARCODE_ASK = 'If you can please pull in the barcodes for us on your end and let us know when that is done, that would be great.';

/** Event details block that the "Other" email starts from. */
export const buildVlSupportDetailsBlock = (details: VlSupportEventDetails): string => detailLines(details);

export function buildVlSupportDraft(
  type: VlSupportEmailType,
  marketplaces: string[],
  details: VlSupportEventDetails,
  syncAccounts?: VlSupportSyncAccount[] | null,
): { subject: string; body: string } {
  if (type === 'other') {
    // Subject and message are typed by the admin; only the event details are filled in.
    return { subject: '', body: `\n\n${detailLines(details)}` };
  }

  if (type === 'need_pid') {
    return {
      subject: 'Need PID',
      body: [PID_INTRO, '', pidDetailLines(details), '', PID_ASK, '', 'Thank you,', 'QuickAsyst'].join('\n'),
    };
  }

  if (type === 'barcode_sync') {
    return {
      subject: 'Need help syncing barcodes',
      body: [
        'Hi Support,',
        '',
        `${describeSyncAttempt(syncAccounts)} for the following event:`,
        '',
        detailLines(details),
        '',
        BARCODE_ASK,
        '',
        'Thank you,',
        'QuickAsyst',
      ].join('\n'),
    };
  }

  const list = marketplaces.length ? joinWithAnd(marketplaces) : '[select a marketplace]';
  const plural = marketplaces.length > 1;
  return {
    subject: `Not populating on ${list}`,
    body: [
      'Hi Support,',
      '',
      `This event is not populating on ${list}:`,
      '',
      detailLines(details),
      '',
      `Can you please look into getting it to populate on ${
        plural ? 'those marketplaces' : 'that marketplace'
      } and provide us a screenshot of proof that it's populating once it's done?`,
      '',
      'Thank you,',
      'QuickAsyst',
    ].join('\n'),
  };
}

// ---------------------------------------------------------------------------
// Bulk requests: several listings of ONE event, or of ONE user across events.
// The email lists events and seats only. A user's name is never part of it.
// ---------------------------------------------------------------------------

export type VlSupportListing = { section: string; row: string; seats: string };

export type VlSupportBulkEvent = {
  eventName: string;
  /** Already formatted for display, e.g. "1/4/2027 7:15 PM". */
  eventDate: string;
  pid: string;
  listings: VlSupportListing[];
};

export type VlSupportBulkDetails = { events: VlSupportBulkEvent[] };

/** Most listings one bulk email can cover (the backend accepts up to 200). */
export const VL_SUPPORT_BULK_MAX_LISTINGS = 200;

/** [5,6,7,9] -> "5-7, 9". Non-numeric seats are kept as written. */
export function compressSeats(seats: Array<string | number | null | undefined>): string {
  const numbers: number[] = [];
  const other: string[] = [];
  for (const raw of seats) {
    const text = String(raw ?? '').trim();
    if (!text) continue;
    if (/^\d+$/.test(text)) numbers.push(Number(text));
    else other.push(text);
  }
  const sorted = Array.from(new Set(numbers)).sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    parts.push(j > i ? `${sorted[i]}-${sorted[j]}` : String(sorted[i]));
    i = j + 1;
  }
  return [...parts, ...Array.from(new Set(other))].join(', ');
}

/** One line per section and row, with that row's seats combined. */
export function groupListings(
  seats: Array<{ section: string; row: string; seat: string | number | null | undefined }>,
): VlSupportListing[] {
  const groups = new Map<string, { section: string; row: string; seats: Array<string | number | null | undefined> }>();
  for (const s of seats) {
    const key = `${s.section}\u0000${s.row}`;
    const group = groups.get(key) || { section: s.section, row: s.row, seats: [] };
    group.seats.push(s.seat);
    groups.set(key, group);
  }
  return Array.from(groups.values()).map((g) => ({
    section: g.section,
    row: g.row,
    seats: compressSeats(g.seats) || 'N/A',
  }));
}

export type BulkScope = { ok: true; kind: 'event' | 'user' } | { ok: false; reason: string };

/** A bulk request must be one event or one user, so it can't turn into an unrelated blast. */
export function bulkSupportScope(
  tickets: Array<{ e_id?: string | null; u_id?: string | null }>,
  max = VL_SUPPORT_BULK_MAX_LISTINGS,
): BulkScope {
  if (!tickets.length) return { ok: false, reason: 'Select the tickets to contact Victory Live about.' };
  if (tickets.length > max) {
    return { ok: false, reason: `A bulk request can cover up to ${max} listings. Narrow the selection.` };
  }
  const same = (key: 'e_id' | 'u_id') => {
    const first = tickets[0]?.[key];
    return Boolean(first) && tickets.every((t) => t[key] === first);
  };
  if (same('e_id')) return { ok: true, kind: 'event' };
  if (same('u_id')) return { ok: true, kind: 'user' };
  return { ok: false, reason: 'Bulk requests are for one event or one user. Select tickets from a single event, or from a single user.' };
}

const listingLines = (listings: VlSupportListing[]): string =>
  listings.map((l) => `  Section ${l.section}, Row ${l.row}, Seats ${l.seats}`).join('\n');

/** Event blocks for the email body: event, date and PID once, then its listings. */
export function buildVlSupportBulkDetailsBlock(bulk: VlSupportBulkDetails): string {
  return bulk.events
    .map((e) => [`Event: ${e.eventName}`, `Event date: ${e.eventDate}`, `PID: ${e.pid}`, 'Listings:', listingLines(e.listings)].join('\n'))
    .join('\n\n');
}

const buildPidBulkBlock = (bulk: VlSupportBulkDetails): string =>
  bulk.events
    .map((e) => [`Event: ${e.eventName}`, `Event date and time: ${e.eventDate}`, 'Listings:', listingLines(e.listings)].join('\n'))
    .join('\n\n');

export const bulkListingCount = (bulk: VlSupportBulkDetails): number =>
  bulk.events.reduce((sum, e) => sum + e.listings.length, 0);

/** Same wording as the single-ticket email, adapted for several listings or events. */
export function buildVlSupportBulkDraft(
  type: VlSupportEmailType,
  marketplaces: string[],
  bulk: VlSupportBulkDetails,
  syncAccounts?: VlSupportSyncAccount[] | null,
): { subject: string; body: string } {
  const block = buildVlSupportBulkDetailsBlock(bulk);
  const eventWord = bulk.events.length > 1 ? 'events' : 'event';

  if (type === 'other') return { subject: '', body: `\n\n${block}` };

  if (type === 'need_pid') {
    return {
      subject: 'Need PID',
      body: [
        bulk.events.length > 1
          ? 'Hi Support, we need your help mapping the correct PIDs for these events.'
          : PID_INTRO,
        '',
        buildPidBulkBlock(bulk),
        '',
        bulk.events.length > 1
          ? 'Can you please provide us the correct PIDs so we can ingest this inventory correctly?'
          : PID_ASK,
        '',
        'Thank you,',
        'QuickAsyst',
      ].join('\n'),
    };
  }

  if (type === 'barcode_sync') {
    return {
      subject: 'Need help syncing barcodes',
      body: [
        'Hi Support,',
        '',
        `${describeSyncAttempt(syncAccounts)} for the following ${eventWord}:`,
        '',
        block,
        '',
        BARCODE_ASK,
        '',
        'Thank you,',
        'QuickAsyst',
      ].join('\n'),
    };
  }

  const list = marketplaces.length ? joinWithAnd(marketplaces) : '[select a marketplace]';
  return {
    subject: `Not populating on ${list}`,
    body: [
      'Hi Support,',
      '',
      `The following ${eventWord} ${bulk.events.length > 1 ? 'are' : 'is'} not populating on ${list}:`,
      '',
      block,
      '',
      `Can you please look into getting ${bulk.events.length > 1 ? 'them' : 'it'} to populate on ${
        marketplaces.length > 1 ? 'those marketplaces' : 'that marketplace'
      } and provide us a screenshot of proof that it's populating once it's done?`,
      '',
      'Thank you,',
      'QuickAsyst',
    ].join('\n'),
  };
}

/** What the Support Tickets page shows for a bulk ticket's event. `ticketCount` is the number of selected tickets (seats). */
export function bulkEventSnapshot(
  bulk: VlSupportBulkDetails,
  ticketCount: number = bulkListingCount(bulk),
): VlSupportEventDetails {
  const first = bulk.events[0];
  const more = bulk.events.length - 1;
  const count = ticketCount;
  return {
    eventName: more > 0 ? `${first.eventName} +${more} more` : first.eventName,
    eventDate: more > 0 ? 'Multiple dates' : first.eventDate,
    pid: bulk.events.length === 1 ? first.pid : 'Multiple',
    section: 'Multiple',
    row: 'Multiple',
    seats: `${count} tickets`,
  };
}

export type VlSupportSummary =
  | { state: 'none' }
  | {
      state: 'contacted';
      sentAt: string;
      type: VlSupportRequest['vsr_type'];
      marketplaces: string[];
      ownerInitials: string | null;
      ticketId: string | null;
      unreadCount: number;
    }
  | { state: 'completed'; completedAt: string | null; ownerInitials: string | null; ticketId: string | null };

/**
 * One card can cover several tickets. It reads as "contacted" while any ticket
 * has an open request, and "completed" once every request on it is closed.
 */
export function summarizeVlSupport(
  tickets: Array<{ vl_support_requests?: VlSupportRequest[] | null }>,
): VlSupportSummary {
  const all = tickets.flatMap((t) => t.vl_support_requests ?? []);
  if (!all.length) return { state: 'none' };
  const open = all
    .filter((r) => r.vsr_status === 'contacted')
    .sort((a, b) => String(b.vsr_sent_at).localeCompare(String(a.vsr_sent_at)));
  if (open.length) {
    return {
      state: 'contacted',
      sentAt: open[0].vsr_sent_at,
      type: open[0].vsr_type,
      marketplaces: Array.from(new Set(open.flatMap((r) => r.vsr_marketplaces ?? []))),
      ownerInitials: open[0].vsr_owner_initials ?? null,
      ticketId: open[0].vsr_ticket_id ?? null,
      unreadCount: Math.max(0, ...open.map((r) => Number(r.ticket?.vst_unread_count || 0))),
    };
  }
  const done = all
    .filter((r) => !!r.vsr_completed_at)
    .sort((a, b) => String(b.vsr_completed_at).localeCompare(String(a.vsr_completed_at)));
  return {
    state: 'completed',
    completedAt: done[0]?.vsr_completed_at ?? null,
    ownerInitials: done[0]?.vsr_owner_initials ?? null,
    ticketId: done[0]?.vsr_ticket_id ?? null,
  };
}

/** Status dropdown on the Listed tab. */
export type VlSupportFilter = 'all' | 'any' | 'contacted' | 'completed' | 'none';

/** Owner dropdown: the whole company, the logged-in admin, or one person's initials. */
export type VlSupportOwnerFilter = 'company' | 'mine' | string;

/**
 * Hasura filter for the Listed tab. `ownerInitials` is the resolved person to
 * limit to (null means the whole company).
 */
export const vlSupportFilterForQuery = (
  filter: VlSupportFilter,
  ownerInitials: string | null = null,
): Record<string, unknown> => {
  const owner = ownerInitials ? { vsr_owner_initials: { _eq: ownerInitials } } : {};
  const withStatus = (status: 'contacted' | 'completed') => ({
    vl_support_requests: { _and: [{ vsr_status: { _eq: status } }, owner] },
  });
  if (filter === 'none') return { _not: { vl_support_requests: {} } };
  if (filter === 'contacted') return withStatus('contacted');
  if (filter === 'completed') {
    return {
      _and: [withStatus('completed'), { _not: { vl_support_requests: { vsr_status: { _eq: 'contacted' } } } }],
    };
  }
  if (filter === 'any') return { vl_support_requests: owner };
  // 'all' only narrows when a person is picked: tickets that person has a request on.
  return ownerInitials ? { vl_support_requests: owner } : {};
};
